import { createTheme, ThemeProvider, Theme as MuiTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import * as React from 'react';
import { createContext, ReactNode, useMemo } from 'react';
import { selectThemes, useAppSelector, selectCurrentPageThemesHash } from '@libs/store';
import { getThemeFonts, getCurrentTheme } from './helper';
import { CorePalette, Theme } from '@libs/types';

const getDesignTokens = (themes?: Theme[], themesHash?: string) => {
    const currentTheme = getCurrentTheme(themes, themesHash);
    const fonts = getThemeFonts(currentTheme);
    return {
        colorSchemes: {
            light: {
                palette: currentTheme?.palette?.light ?? {},
            },
            dark: {
                palette: currentTheme?.palette?.dark ?? {},
            },
        },
        colors: currentTheme?.colors ?? {},
        styles: currentTheme?.styles ?? {},
        components: {
            MuiCssBaseline: {
                styleOverrides: `
                 ${fonts}
                  @media (prefers-color-scheme: light) {
                    body {
                       background: #FFF;
                       color: #141414;
                    }
                  }
                  @media (prefers-color-scheme: dark) {
                    body {
                        background: #141414;
                        color: #FFF;
                    }
                }
             `,
            },
        },
    } as unknown as MuiTheme;
};

interface ProviderProps {
    children: ReactNode;
}
interface ThemeWithColorSchemes {
    colorSchemes?: {
        light?: { palette?: CorePalette };
        dark?: { palette?: CorePalette };
    };
    colors?: Theme['colors'];
    styles?: Theme['styles'];
}

const CustomThemeContext = createContext<ThemeWithColorSchemes | Record<string, never>>({});

const CoreProvider = ({ children }: ProviderProps) => {
    const pageThemesHash = useAppSelector(selectCurrentPageThemesHash);
    const reduxThemes = useAppSelector(selectThemes);
    const coreTheme = useMemo(() => createTheme(getDesignTokens(reduxThemes, pageThemesHash)), [pageThemesHash, reduxThemes]);

    return (
        <ThemeProvider theme={coreTheme} noSsr={true}>
            <CustomThemeContext value={coreTheme as unknown as ThemeWithColorSchemes}>
                <CssBaseline />
                {children}
            </CustomThemeContext>
        </ThemeProvider>
    );
};

export { CoreProvider, CustomThemeContext };
